import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { client } from "@/services/client"
import type { Paths } from "@/services/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import { fetchOrThrow } from "@/utils/api"
import { keepPreviousData, useQuery } from "@tanstack/react-query"

import type { VarianceDetailPayload } from "../pages/variance-route/variance.types"
import type { WindowQuery } from "./use-window-query"
import { useWindowQuery } from "./use-window-query"

// insights-api models `organization_id` (required int) on the /api/v1/neat
// operations, so use the generated query type directly.
//
// `t_peer` (the peer-percentile sensitivity threshold tuned by
// `SensitivityCog`) is already honored by insights-api and forwarded by the
// BFF passthrough, but the generated schema hasn't been regenerated to
// include it yet — extend the query type locally until the next
// `types:generate:insights-api` run picks it up.
type VarianceQuery = NonNullable<
  Paths["/api/v1/neat/insights/{slug}"]["get"]["parameters"]["query"]
> & {
  t_peer?: number
  window?: string
  start?: string
  end?: string
}

const T_PEER_MIN = 50
const T_PEER_MAX = 99

const clampTPeer = (tPeer: number): number =>
  Math.min(T_PEER_MAX, Math.max(T_PEER_MIN, tPeer))

const getVariance = async (
  organizationId: number,
  win: WindowQuery,
  tPeer?: number
): Promise<VarianceDetailPayload> => {
  const query: VarianceQuery = { organization_id: organizationId, ...win }
  if (tPeer != null) {
    query.t_peer = tPeer
  }

  // BFF path is templated `/api/v1/neat/insights/{slug}` — pass slug via path params.
  // Field names align with the generated response; typed pass-through.
  return (await fetchOrThrow(() =>
    client().GET("/api/v1/neat/insights/{slug}", {
      params: { path: { slug: "variance" }, query },
    })
  )) as VarianceDetailPayload
}

/**
 * The display window is NOT a parameter — it is read from the URL via
 * `useWindowQuery`, the same source every other insight fetch uses, so a
 * window chosen on `/neat` applies here too instead of this page starting
 * over at its own trailing-12-month default.
 *
 * @param tPeer Peer-percentile sensitivity threshold (50-99, clamped),
 * ephemeral FE state owned by `SensitivityCog`. Omit to use the server
 * default. Included in the query key so a changed threshold refetches
 * rather than reusing the previous threshold's cached response.
 *
 * `placeholderData: keepPreviousData` keeps the previously-rendered rows
 * on screen while the new threshold's request is in flight, instead of
 * dropping to the `isPending` loading state — a `t_peer` change is an
 * in-place refinement of the same table, not a full page reload.
 */
export const useGetVariance = (tPeer?: number) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const win = useWindowQuery()
  const resolvedTPeer = tPeer == null ? undefined : clampTPeer(tPeer)
  const queryKey = useQueryKey(ApiQueryName.SiteVariance, "getOne", null, {
    ...win,
    tPeer: resolvedTPeer,
  })
  const queryResult = useQuery<VarianceDetailPayload>({
    queryKey,
    enabled: activeOrganizationId != null,
    placeholderData: keepPreviousData,
    queryFn: () =>
      getVariance(Number(activeOrganizationId), win, resolvedTPeer),
  })
  return useRenameQueryResult(queryResult, ApiQueryName.SiteVariance)
}
